import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import classNames from 'classnames';
import { Fragment } from 'react';
import ReactMarkdown from 'react-markdown';
import { useSelector } from 'react-redux';
import Dot from '/@/components/dot';
import References from '/@/components/references';
import colors from '/@/components/theme-provider/colors.js';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { getUser } from '/@/store/session/selectors';
import { range } from '/@/utils';
import Dots from '/@/v3/components/dots';
import { NewDotsReference } from '/@/v3/components/new-dots-reference';
import { RangeReference } from '/@/v3/components/range-reference';
import Title from '/@/v3/components/title';
import styles from './styles.module.css';

export default function NewMetrics({ products, dataKey, dots, brand, country, internal }) {
    const referenceCodenames = [];
    const user = useSelector(getUser);

    if (dots) {
        products.forEach((product) =>
            product.metrics.forEach(
                (metric) =>
                    metric.referenceCodename !== 'default' &&
                    !referenceCodenames.includes(metric.referenceCodename) &&
                    referenceCodenames.push(metric.referenceCodename)
            )
        );
    }
    const useDefaultReference = referenceCodenames.length === 0;

    const data = products.map((prod) => {
        return prod[dataKey];
    });

    const firstHybrid = data[0];
    const secondHybrid = data[1];
    const thirdHybrid = data[2];
    const forthHybrid = data[3];

    let parsedData = [];

    const column = data.map((dat) =>
        dat.map((datum) => {
            return datum.id;
        })
    );

    column.map((col) => {
        col.map((c) => {
            return parsedData.push({ id: c });
        });
    });

    parsedData = Array.from(new Set(parsedData.map(JSON.stringify))).map(JSON.parse);

    const addData = (array, propName) => {
        parsedData.map((p) =>
            array.map((a) => {
                if (p.id === a.id) {
                    const val = a.value;
                    p[propName] = val;
                    p['name'] = a.name;
                    if (a.references) {
                        p['references'] = a.references;
                    }
                }
            })
        );
    };

    function isValInRange(value, rangeArr) {
        const range = rangeArr.find((ra) => {
            return value >= ra[0] && value <= ra[1];
        });
        return rangeArr.indexOf(range);
    }

    const hasReference = (data, p) => {
        const ref = [];
        if (p.references && p.references.length) {
            let references = p.references;
            references = references.map((r) => {
                const min = r.min !== null ? r.min : Number.NEGATIVE_INFINITY;
                const max = r.max !== null ? r.max : Number.POSITIVE_INFINITY;
                return ref.push([min, max]);
            });
            return isValInRange(data, ref);
        }
    };

    const writtenValue = (data, p) => {
        const referenceIndex = hasReference(_(data), p);
        let doubleReference = _(data).split('-');
        doubleReference = doubleReference.filter((r) => {
            return r !== '';
        });

        return ['CONSULTE A BULA', 'VER FOLLETO', 'SEE BROCHURE'].some((el) => el == _(data).toUpperCase()) ? (
            <a
                className={styles.link}
                href='https://agrofit.agricultura.gov.br/agrofit_cons/principal_agrofit_cons'
                target='_blank'
                rel='noreferrer'
            >
                {`${_(data)}*`}
            </a>
        ) : doubleReference.length === 2 ? (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 5 }}>
                {doubleReference.map((r, i) => {
                    const referenceI = hasReference(r, p);

                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <ReactMarkdown
                                className={classNames(
                                    styles.markdown,
                                    styles.doubleReference,
                                    (Boolean(referenceI >= 0) || (p.references && p.references.length === 4)) &&
                                        styles.references,
                                    (Boolean(referenceI >= 0) || (p.references && p.references.length === 4)) &&
                                        styles[
                                            `reference-${
                                                p.references && p.references.length === 3
                                                    ? 'other'
                                                    : p.references.length === 4
                                                      ? 'toxicology'
                                                      : 'default'
                                            }-${p.references && p.references.length === 4 ? r : referenceI}`
                                        ]
                                )}
                            >
                                {r}
                            </ReactMarkdown>
                            {r !== doubleReference[doubleReference.length - 1] && ' - '}
                        </div>
                    );
                })}
            </div>
        ) : (
            <ReactMarkdown
                className={classNames(
                    styles.markdown,
                    (Boolean(referenceIndex >= 0) || (p.references && p.references.length === 4)) && styles.references,
                    (Boolean(referenceIndex >= 0) || (p.references && p.references.length === 4)) &&
                        styles[
                            `reference-${
                                p.references && p.references.length === 3
                                    ? 'other'
                                    : p.references.length === 4
                                      ? 'toxicology'
                                      : 'default'
                            }-${p.references && p.references.length === 4 ? _(data) : referenceIndex}`
                        ]
                )}
            >
                {_(data)}
            </ReactMarkdown>
        );
    };

    addData(firstHybrid, 'first');
    secondHybrid && addData(secondHybrid, 'second');
    thirdHybrid && addData(thirdHybrid, 'third');
    forthHybrid && addData(forthHybrid, 'forth');

    return (
        <Fragment>
            <Fragment>
                {dots && (
                    <Grid sx={{ py: 3 }} container justifyContent='center' alignItems='center'>
                        <Grid item xs={12} md={10}>
                            {useDefaultReference ? (
                                <NewDotsReference brand={brand} country={country} />
                            ) : (
                                <RangeReference
                                    hideTitle
                                    maxWidth={false}
                                    referenceCodenames={referenceCodenames}
                                    theme={brand.theme}
                                />
                            )}
                        </Grid>
                    </Grid>
                )}
            </Fragment>

            <TableContainer sx={{ width: '100%', boxShadow: 'none' }} component={Paper}>
                <Table
                    sx={{ minWidth: 650, borderCollapse: 'separate', borderSpacing: '0 10px' }}
                    aria-label='simple table'
                >
                    <TableHead sx={{ '& tr th': { border: 0 } }}>
                        <TableRow>
                            <TableCell sx={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: 12, position: 'relative' }}>
                                {internal && (
                                    <div className={`${styles.watermark} mp-mask`}>
                                        {range(10).map(() => `${user.email} `)}
                                    </div>
                                )}
                            </TableCell>
                            {products.map((prod) => {
                                return (
                                    <TableCell
                                        key={prod.id}
                                        sx={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: 12, position: 'relative' }}
                                        align='center'
                                    >
                                        <Title
                                            bolder
                                            theme={products?.[0]?.id === prod.id ? settings.app.theme : undefined}
                                            className={classNames(
                                                styles.title,
                                                products?.[0]?.id !== prod.id ? styles.noSelected : undefined
                                            )}
                                        >
                                            {prod.product ? prod.product.catalogName || prod.product.name : prod.name}
                                        </Title>
                                        {internal && (
                                            <div className={`${styles.watermark} mp-mask`}>
                                                {range(10).map(() => `${user.email} `)}
                                            </div>
                                        )}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {parsedData.map((product) => {
                            return (
                                <TableRow
                                    key={_(product.id)}
                                    sx={{
                                        fontSize: 14,
                                        td: {
                                            border: '1px solid #E1E1E1',
                                            borderLeft: 0,
                                        },
                                        'td:first-child': {
                                            borderLeft: '1px solid #E1E1E1',
                                            borderTopLeftRadius: '8px',
                                            borderBottomLeftRadius: '8px',
                                            borderTopRightRadius: 0,
                                            borderBottomRightRadius: 0,
                                        },
                                        'td:last-child': {
                                            borderLeft: 0,
                                            borderTopLeftRadius: 0,
                                            borderBottomLeftRadius: 0,
                                            borderTopRightRadius: '8px',
                                            borderBottomRightRadius: '8px',
                                        },
                                        '&:hover .MuiTableCell-root': {
                                            backgroundColor: colors.themes[settings.app.theme].primary[100],
                                        },
                                    }}
                                >
                                    <TableCell sx={{ width: '35%', position: 'relative' }} scope='row'>
                                        {product.references && product.references.length ? (
                                            <div className={styles.infoContainer}>
                                                <div>{_(product.name)}</div>
                                                <Tooltip
                                                    placement='top'
                                                    title={<References product={product} />}
                                                    arrow
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                backgroundColor: 'white',
                                                                color: 'black',
                                                                boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                                                                minWidth: '400px',
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <div>
                                                        <span style={{ fontSize: '16px' }} className='material-icons'>
                                                            info
                                                        </span>
                                                    </div>
                                                </Tooltip>
                                            </div>
                                        ) : (
                                            _(product.name)
                                        )}
                                        {internal && (
                                            <div className={`${styles.watermark} mp-mask`}>
                                                {range(10).map(() => `${user.email} `)}
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            position: 'relative',
                                            '&:not(:hover)': {
                                                backgroundColor: '#F8F8F8',
                                            },
                                        }}
                                        align='center'
                                    >
                                        {_(product.first) ? (
                                            dataKey === 'metrics' ? (
                                                <Fragment>
                                                    {country.code === 'BR' || country.code === 'PY' ? (
                                                        <Dots
                                                            max={brand.metricsMaxValue}
                                                            center
                                                            value={Number(_(product.first))}
                                                            showValues={brand.showMetricsValues}
                                                        />
                                                    ) : (
                                                        <Dot
                                                            country={country}
                                                            max={brand.metricsMaxValue}
                                                            value={_(product.first)}
                                                        />
                                                    )}
                                                </Fragment>
                                            ) : (
                                                writtenValue(product.first, product)
                                            )
                                        ) : (
                                            '-'
                                        )}
                                        {internal && (
                                            <div className={`${styles.watermark} mp-mask`}>
                                                {range(10).map(() => `${user.email} `)}
                                            </div>
                                        )}
                                    </TableCell>
                                    {secondHybrid && (
                                        <TableCell align='center' sx={{ position: 'relative' }}>
                                            {_(product.second) ? (
                                                dataKey === 'metrics' ? (
                                                    <Fragment>
                                                        {country.code === 'BR' || country.code === 'PY' ? (
                                                            <Dots
                                                                max={brand.metricsMaxValue}
                                                                center
                                                                value={Number(_(product.second))}
                                                                showValues={brand.showMetricsValues}
                                                            />
                                                        ) : (
                                                            <Dot
                                                                country={country}
                                                                max={brand.metricsMaxValue}
                                                                value={_(product.second)}
                                                            />
                                                        )}
                                                    </Fragment>
                                                ) : (
                                                    writtenValue(product.second, product)
                                                )
                                            ) : (
                                                '-'
                                            )}
                                            {internal && (
                                                <div className={`${styles.watermark} mp-mask`}>
                                                    {range(10).map(() => `${user.email} `)}
                                                </div>
                                            )}
                                        </TableCell>
                                    )}
                                    {thirdHybrid && (
                                        <TableCell align='center' sx={{ position: 'relative' }}>
                                            {_(product.third) ? (
                                                dataKey === 'metrics' ? (
                                                    <Fragment>
                                                        {country.code === 'BR' || country.code === 'PY' ? (
                                                            <Dots
                                                                max={brand.metricsMaxValue}
                                                                center
                                                                value={Number(_(product.third))}
                                                                showValues={brand.showMetricsValues}
                                                            />
                                                        ) : (
                                                            <Dot
                                                                country={country}
                                                                max={brand.metricsMaxValue}
                                                                value={_(product.third)}
                                                            />
                                                        )}
                                                    </Fragment>
                                                ) : (
                                                    writtenValue(product.third, product)
                                                )
                                            ) : (
                                                '-'
                                            )}
                                            {internal && (
                                                <div className={`${styles.watermark} mp-mask`}>
                                                    {range(10).map(() => `${user.email} `)}
                                                </div>
                                            )}
                                        </TableCell>
                                    )}
                                    {forthHybrid && (
                                        <TableCell align='center' sx={{ position: 'relative' }}>
                                            {_(product.forth) ? (
                                                dataKey === 'metrics' ? (
                                                    <Fragment>
                                                        {country.code === 'BR' || country.code === 'PY' ? (
                                                            <Dots
                                                                max={brand.metricsMaxValue}
                                                                center
                                                                value={Number(_(product.forth))}
                                                                showValues={brand.showMetricsValues}
                                                            />
                                                        ) : (
                                                            <Dot
                                                                country={country}
                                                                max={brand.metricsMaxValue}
                                                                value={_(product.forth)}
                                                            />
                                                        )}
                                                    </Fragment>
                                                ) : (
                                                    writtenValue(product.forth, product)
                                                )
                                            ) : (
                                                '-'
                                            )}
                                            {internal && (
                                                <div className={`${styles.watermark} mp-mask`}>
                                                    {range(10).map(() => `${user.email} `)}
                                                </div>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Fragment>
    );
}
